/* ========================================================================
   © 2026 SpeedToLead Framework. All rights reserved.
   ======================================================================== */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
    const navigate = useNavigate();
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        const sessionId = m ? decodeURIComponent(m[1]) : null;

        // Strip the fragment before doing anything so a refresh doesn't re-trigger
        const cleanPath = window.location.pathname + window.location.search;
        window.history.replaceState({}, document.title, cleanPath);

        if (!sessionId) {
            navigate("/", { replace: true });
            return;
        }

        api.post("/auth/session", { session_id: sessionId }, { withCredentials: true })
            .then(async ({ data }) => {
                const intent = sessionStorage.getItem("speedtolead.postLoginRedirect");
                sessionStorage.removeItem("speedtolead.postLoginRedirect");
                if (intent) {
                    navigate(intent, { replace: true, state: { user: data.user } });
                    return;
                }
                // Auto-jump into the workspace if the user has one (paid or admin-granted)
                try {
                    const { data: ws } = await api.get("/auth/my-workspace", { withCredentials: true });
                    if (ws?.hasWorkspace && ws.webhookToken) {
                        navigate(`/secure-ops-hub-99x/${ws.webhookToken}`, {
                            replace: true,
                            state: { user: data.user },
                        });
                        return;
                    }
                } catch (_) { /* fall through to home */ }
                navigate("/", { replace: true, state: { user: data.user } });
            })
            .catch(() => {
                navigate("/", { replace: true });
            });
    }, [navigate]);

    return (
        <div
            data-testid="auth-callback"
            className="grid min-h-screen place-items-center bg-background text-foreground"
        >
            <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    Authenticating · please wait
                </p>
            </div>
        </div>
    );
}
